/**
 * @overview WorkspaceProvider wrapper that takes a slug or id and creates a new context tree with that workspace
 */

import gql from 'graphql-tag'
import { get, isEqual } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { graphql } from 'react-apollo'
import WorkspaceContext from './context'

class WorkspaceProvider extends React.Component {
  static propTypes = {
    /** An error object from the Apollo query */
    error: PropTypes.object,
    /** Reason for access denial */
    accessDeniedReason: PropTypes.oneOf([
      'unspecified',
      'sso_required',
      'wrong_sso_connection',
      'mfa_required',
      undefined
    ]),
    /** Whether or not the workspace data is loading */
    isLoading: PropTypes.bool,
    /** Whether or not the workspace could be found (convenience property instead of checking `workspace == null`) */
    isNotFound: PropTypes.bool,
    /** A function used to refresh the workspace */
    refreshWorkspace: PropTypes.func,
    /** The fetched workspace */
    workspace: PropTypes.shape({
      /** The workspace id */
      id: PropTypes.string.isRequired,
      /** The workspace slug */
      slug: PropTypes.string.isRequired,
      /** The name of the workspace */
      name: PropTypes.string.isRequired,
      /** Whether or not the current user is the workspace owner */
      isCurrentUserOwner: PropTypes.bool,
      /** Whether or not the current user is a read-only member of the workspace */
      isCurrentUserMember: PropTypes.bool,
      /** The workspace's billing details */
      billing: PropTypes.shape({
        /** The billing plan id */
        planId: PropTypes.string.isRequired,
        /** The billing plan name */
        planName: PropTypes.string.isRequired,
        /** Whether or not the workspace plan is still a trial */
        inTrial: PropTypes.bool,
        /** Whether or not the workspace plan is free */
        isFreeAccount: PropTypes.bool,
        /** Whether or not the workspace plan is considered Business Tier */
        isOnBusinessTier: PropTypes.bool,
        /** Whether or not the workspace is on an invoiced plan */
        isOnInvoicedPlan: PropTypes.bool,
        /** Whether or not the workspace plan is considered Self-Service */
        isOnSelfServicePlan: PropTypes.bool,
        /** Whether or not the workspace plan is in the Startup Program */
        isOnStartupProgram: PropTypes.bool,
        /**
         * Whether or not this workspace is using the multi-level billing plans
         * All legacy workspaces will return false for this.
         */
        usingTieredBilling: PropTypes.bool
      })
    }),
    workspaceId: PropTypes.string,
    workspaceSlug: PropTypes.string
  }

  static defaultProps = {
    error: null,
    accessDeniedReason: undefined,
    isLoading: false,
    isNotFound: false,
    workspace: null,
    refreshWorkspace: async () => {}
  }

  static getDerivedStateFromProps(
    {
      error,
      isLoading,
      accessDeniedReason,
      isNotFound,
      workspace,
      workspaceId,
      workspaceSlug,
      refreshWorkspace
    },
    state
  ) {
    const context = {
      error,
      isLoading,
      accessDeniedReason,
      isNotFound,
      workspace,
      workspaceId,
      workspaceSlug,
      refreshWorkspace
    }

    /**
     * Use `this.state` for context to avoid unnecessary re-renders in consumers
     * If the values are the same, don't update the object in state
     * @see {@link https://reactjs.org/docs/context.html#caveats}
     */
    if (isEqual(context, state)) {
      return null
    }

    return context
  }

  state = {}

  render() {
    return (
      <WorkspaceContext.Provider value={this.state}>
        {this.props.children}
      </WorkspaceContext.Provider>
    )
  }
}

const withWorkspaceData = graphql(
  gql`
    query app_WorkspaceContext($workspaceId: ID, $workspaceSlug: Slug) {
      workspace: authzWorkspace(id: $workspaceId, slug: $workspaceSlug) {
        id
        slug
        name
        isCurrentUserOwner
        isCurrentUserMember
        billing {
          planId
          planName
          inTrial
          isFreeAccount
          isOnBusinessTier
          isOnInvoicedPlan
          isOnSelfServicePlan
          isOnStartupProgram
          usingTieredBilling
        }
        entitlements {
          quotas {
            connections {
              sources
            }
          }
          features {
            warehouseSelectiveSyncs
          }
        }
      }
    }
  `,
  {
    options({ workspaceId, workspaceSlug }) {
      return {
        variables: {
          workspaceId,
          workspaceSlug
        }
      }
    },
    // Only fetch workspace data when either an id or slug are provided
    skip({ workspaceId, workspaceSlug }) {
      return !workspaceId && !workspaceSlug
    },
    props({ data, ownProps }) {
      const error = data.error || null
      const isLoading = data.loading
      const workspace = data.workspace
      let accessDeniedReason

      // Only classify as "access denied" when we get an error with that message
      // Or when the user is trying to access a workspace different than the one used to SSO
      const isAccessDenied =
        error && /Workspace access denied/i.test(error.message)

      if (isAccessDenied) {
        const groups = /ReasonHint:\s+(\w+)/i.exec(error.message)
        if (groups) {
          accessDeniedReason = groups[1]
        } else {
          accessDeniedReason = 'unspecified' // TODO: Remove after authz or gateway-api is updated
        }
      }

      // Only classify as "not found" when done loading and without an error
      const isNotFound =
        (error && /Workspace not found/.test(error.message)) ||
        (!isLoading && !workspace)

      return {
        error,
        accessDeniedReason,
        isLoading,
        isNotFound,
        workspace,
        workspaceId: ownProps.workspaceId || get(workspace, 'id'),
        workspaceSlug: ownProps.workspaceSlug || get(workspace, 'slug'),
        refreshWorkspace: data.refetch
      }
    }
  }
)

export default withWorkspaceData(WorkspaceProvider)
